import axiosInstance from './axios';
import { apiBaseURL } from 'config/axios';
import Utils from 'services/Utils';
import { Paginated } from './Models';
import { Cliente } from './ClientesService';

const entityApiRoute = 'certificados';

// export interface Certificado {
//   id: number;
//   cliente: Cliente;
//   cantidad?: number;
//   avisoPorMail: boolean;
//   imprimirFechaDeVencimiento: boolean;
//   protocolo?: string;
//   fechaAno: string;
//   fechaMes: string;
//   fechaTratamiento: Date;
//   fechaVencimiento: Date;
//   filename: string;
//   tipo: string;
//   tipo_certificado: TipoCertificado;
//   url: string;
// }

export interface Certificado {
  id: number;
  cliente_id: number;
  cantidad: number;
  avisoPorMail: boolean;
  imprimirFechaDeVencimiento: boolean;
  protocolo: string;
  fechaAno: string;
  fechaMes: string;
  fechaTratamiento: Date;
  fechaVencimiento: Date;
  fileName: string;
  tipo: string;
  url: string;
  created_at: Date;
  updated_at: Date;
  tipo_certificado: TipoCertificado;
  cliente: Cliente;
}

export interface CertificadoPOST {
  cliente_id: number;
  cantidad?: number;
  avisoPorMail: boolean;
  imprimirFechaDeVencimiento?: boolean;
  protocolo?: string;
  fechaAno: string;
  fechaMes: string;
  fechaTratamiento: Date;
  fechaVencimiento?: Date;
  filename: string;
  tipo: string;
  url?: string;
}

export interface TipoCertificado {
  id: number;
  key: string;
  description: string;
  color: string;
  forecolor: string;
}

export interface Oblea {
  id: number;
  tipoCertificado: string;
  fechaAno: string;
  fechaMes: string;
  domicilio: string;
  fechaTratamiento: Date;
  fechaVencimiento: Date;
  empresa: string;
  nroRegistroEmpresa: string;
  dt: string;
  nroRegistroDT: string;
  productosUtilizados: string;
  estadoTanque?: string;
  resultadoBacteriologico?: string;
}

const createEntity = async (entity: CertificadoPOST): Promise<Certificado> => {
  const url = `${apiBaseURL}/${entityApiRoute}`;
  const result = await axiosInstance.post(url, entity);
  return result.data;
}

const updateEntity = async (id: string, entity: CertificadoPOST) => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}`;
  await axiosInstance.patch(url, entity);
}

const deleteEntity = async (id: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}`;
  await axiosInstance.delete(url);
}

const fetchPaginated = async (page: number, filter: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}?page=${page}&filter=${filter}`;
  const response = await axiosInstance.get(url);
  const result: Paginated<Certificado> = response.data?.result;
  return result;
}

const fetchOne = async (id: string): Promise<Certificado | undefined> => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}`;
  try {
    const response = await axiosInstance.get(url);
    let entity = response.data?.result;
    entity = Utils.removeNullValues(entity);
    return entity;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("No se encontraron datos con el ID proporcionado");
    }
    throw new Error("Ocurrio un error al obtener el dato. " + error.message);
  }
}

const fetchAll = async () => {
  const url = `${apiBaseURL}/${entityApiRoute}`;
  const response = await axiosInstance.get(url);
  const result: Certificado[] = response.data?.result;
  return result;
}

const fetchTiposDeCertificado = async () => {
  const url = `${apiBaseURL}/tipos-certificado`;
  const response = await axiosInstance.get(url);
  const result: TipoCertificado[] = response.data?.result;
  return result;
}

const uploadPDF = async (file, clienteId: string, filename: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}/upload`;
  let formData = new FormData();
  formData.append("archivo", file);
  formData.append("cliente_id", clienteId);
  formData.append("filename", filename);
  const response = await axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    }
  });
  return response;
}

const uploadReporteOperativo = async (files, clienteId: string, base_filenames: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}/upload-reporte`;
  let formData = new FormData();
  files.forEach(file => {
    formData.append("archivos[]", file);
  });
  formData.append("cliente_id", clienteId);
  formData.append("base_filename", base_filenames);
  const response = await axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    }
  });
  return response;
}

const uploadProductosHabilitados = async (files) => {
  const url = `${apiBaseURL}/productos/habilitados`;
  let formData = new FormData();
  files.forEach(file => {
    formData.append("archivos[]", file);
  });
  const response = await axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    }
  });
  return response;
}

const getCertificadoPdfUrl = (filename: string, id: number) => {
  const url = filename.startsWith('https://')
    ? filename
    : `${apiBaseURL}/certificados/${id}/download`;
  return url;
}

const getReporteOperativoPdfUrl = (filename: string, id: number) => {
  const url = `${apiBaseURL}/certificados/${id}/reporte-operativo/${filename}`;
  return url;
}

const getReporteOperativoFiles = async (id: number) => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}/reporte-operativo`;
  const response = await axiosInstance.get(url);
  const result: string[] = response.data?.result;
  return result;
}

const getProductosHabilitadosFiles = async (filterText: string) => {
  const url = `${apiBaseURL}/productos/habilitados`;
  const response = await axiosInstance.get(url);
  const result: string[] = response.data?.result;
  let filteredResult = result;
  if (filterText) {
    filteredResult = result.filter(str => {
      return str.toLowerCase().indexOf(filterText.toLowerCase()) !== -1;
    })
  }
  return filteredResult.sort();
}

const getProductosHabilitadosPdfUrl = (filename: string) => {
  const url = `${apiBaseURL}/productos/habilitados/${filename}`;
  return url;
}

const deleteProductosHabilitadosFile = async (filename: string) => {
  const url = `${apiBaseURL}/productos/habilitados/${filename}`;
  await axiosInstance.delete(url);
}

const deleteProductosHabilitadosAllFiles = async () => {
  const url = `${apiBaseURL}/productos/habilitados`;
  const message = await axiosInstance.delete(url);
  return message;
}

const fetchOblea = async (id: string): Promise<Oblea | undefined> => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}/oblea`;
  try {
    const response = await axiosInstance.get(url);
    let entity = response.data?.result;
    entity = Utils.removeNullValues(entity);
    return entity;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("No se encontraron datos con el ID proporcionado");
    }
    throw new Error("Ocurrio un error al obtener el dato. " + error.message);
  }
}

export default {
  createEntity,
  updateEntity,
  deleteEntity,
  fetchPaginated,
  fetchOne,
  fetchAll,
  fetchTiposDeCertificado,
  uploadPDF,
  uploadReporteOperativo,
  getReporteOperativoFiles,
  getReporteOperativoPdfUrl,
  getCertificadoPdfUrl,
  fetchOblea,
  uploadProductosHabilitados,
  getProductosHabilitadosFiles,
  getProductosHabilitadosPdfUrl,
  deleteProductosHabilitadosFile,
  deleteProductosHabilitadosAllFiles
}
